import { Injectable, signal } from '@angular/core';

import { readLastOrganizationId } from '@/shared/utils/organization-route';

export interface ProvisioningAttempt {
  scope: string;
  userId: string;
  organizationId: string;
  idempotencyKey: string;
  operationId: string | null;
  state: string;
}

const STORAGE_KEY = 'hubnari.provisioning-attempts.v1';
const TERMINAL_STATES = new Set(['completed', 'abandoned']);

@Injectable({ providedIn: 'root' })
export class ProvisioningAttempts {
  private readonly attempts = signal<ProvisioningAttempt[]>(this.restore());
  private activeUserId: string | null = null;

  activateUser(userId: string): void {
    this.activeUserId = userId;
    // A restored tab may have been used by another account. Retain only this
    // user's operation handles so a same-user reload can still recover them.
    this.save(this.attempts().filter((attempt) => attempt.userId === userId));
  }

  clearUser(): void {
    this.activeUserId = null;
    this.save([]);
  }

  forOrganization(organizationId: string | null | undefined): ProvisioningAttempt[] {
    return organizationId && this.activeUserId
      ? this.attempts().filter(
          (attempt) =>
            attempt.organizationId === organizationId && attempt.userId === this.activeUserId,
        )
      : [];
  }

  keyFor(method: string, path: string): ProvisioningAttempt {
    const organizationId = readLastOrganizationId();
    const userId = this.activeUserId;
    if (!organizationId || !userId) {
      throw new Error('Aguarde a identificação da sessão e da organização antes de provisionar.');
    }
    // Scope is deliberately free of request bodies, which may contain passwords.
    const scope = `${userId}:${organizationId}:${method}:${path}`;
    const existing = this.attempts().find((attempt) => attempt.scope === scope);
    if (existing) return existing;
    const attempt: ProvisioningAttempt = {
      scope,
      userId,
      organizationId,
      idempotencyKey: crypto.randomUUID(),
      operationId: null,
      state: 'sending',
    };
    this.save([...this.attempts(), attempt]);
    return attempt;
  }

  isBlocked(attempt: ProvisioningAttempt): boolean {
    return attempt.operationId !== null;
  }

  ownsAttempt(attempt: ProvisioningAttempt): boolean {
    return (
      this.activeUserId === attempt.userId &&
      this.attempts().some(
        (current) =>
          current.scope === attempt.scope && current.idempotencyKey === attempt.idempotencyKey,
      )
    );
  }

  recordOperation(scope: string, operationId: string, state: string): void {
    this.update(scope, { operationId, state });
  }

  recordUnknown(scope: string): void {
    this.update(scope, { state: 'unknown' });
  }

  recordStatus(operationId: string, state: string): void {
    const attempt = this.attempts().find((item) => item.operationId === operationId);
    if (attempt) this.update(attempt.scope, { state });
  }

  discard(scope: string): void {
    this.save(this.attempts().filter((attempt) => attempt.scope !== scope));
  }

  canAcknowledge(attempt: ProvisioningAttempt): boolean {
    return TERMINAL_STATES.has(attempt.state);
  }

  private update(scope: string, changes: Partial<ProvisioningAttempt>): void {
    this.save(
      this.attempts().map((attempt) =>
        attempt.scope === scope ? { ...attempt, ...changes } : attempt,
      ),
    );
  }

  private save(attempts: ProvisioningAttempt[]): void {
    this.attempts.set(attempts);
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempts));
    } catch {
      // A private browser session still retains the in-memory guard.
    }
  }

  private restore(): ProvisioningAttempt[] {
    try {
      const value: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? '[]');
      if (!Array.isArray(value)) return [];
      return value
        .filter(
          (item): item is ProvisioningAttempt =>
            typeof item === 'object' &&
            item !== null &&
            typeof item.scope === 'string' &&
            typeof item.userId === 'string' &&
            typeof item.organizationId === 'string' &&
            typeof item.idempotencyKey === 'string' &&
            (item.operationId === null || typeof item.operationId === 'string') &&
            typeof item.state === 'string',
        )
        .map((attempt) =>
          attempt.state === 'sending' ? { ...attempt, state: 'unknown' } : attempt,
        );
    } catch {
      return [];
    }
  }
}
